import { ComponentType, SVGProps, useState } from "react"
import Icon from "../../Icon/Icon"
import styles from "./SwitchButton.module.css"
import InfoTooltip from "../../Tooltips/InfoTooltip/InfoTooltip"

interface SwitchButtonProps {
	firstOption: string,
	secondOption: string,
	firstSelected: boolean,
	firstOptionIcon?: ComponentType<SVGProps<SVGSVGElement>>,
	secondOptionIcon?: ComponentType<SVGProps<SVGSVGElement>>,
	firstDisabled?: boolean,
	secondDisabled?: boolean,
	firstTooltip?: boolean,
	secondTooltip?: boolean,
	firstTooltipProps?: { text: string, buttonLabel: string, onAction: () => void },
	secondTooltipProps?: { text: string, buttonLabel: string, onAction: () => void },
	firstOptionTooltipOnHover?: boolean,
	secondOptionTooltipOnHover?: boolean,
	firstOptionCallback: () => void,
	secondOptionCallback: () => void
}

export default function SwitchButton ({
	firstOption,
	secondOption,
	firstSelected,
	firstOptionIcon,
	secondOptionIcon,
	firstDisabled = false, 
	secondDisabled = false,
	firstTooltip = false,
	secondTooltip = false,
	firstTooltipProps,
	secondTooltipProps,
	firstOptionTooltipOnHover = false,
	secondOptionTooltipOnHover = false,
	firstOptionCallback,
	secondOptionCallback }: SwitchButtonProps) {
	  const [firstAnchorEl, setFirstAnchorEl] = useState<HTMLElement | null>(null);
	  const [secondAnchorEl, setSecondAnchorEl] = useState<HTMLElement | null>(null);

	function firstOptionClick(e: React.MouseEvent<HTMLDivElement>) {
		if (!firstDisabled) firstOptionCallback()
		else if (firstTooltip && !firstOptionTooltipOnHover) setFirstAnchorEl(e.currentTarget)
	}

	function secondOptionClick(e: React.MouseEvent<HTMLDivElement>) {
		if (!secondDisabled) secondOptionCallback()
		else if (secondTooltip && !secondOptionTooltipOnHover) {
			setSecondAnchorEl(e.currentTarget)
		}
	}

	function handleFirstOptionMouseEnter(e: React.MouseEvent<HTMLDivElement>) {
		if (firstTooltip && firstOptionTooltipOnHover && firstDisabled) {
			setFirstAnchorEl(e.currentTarget)
		}
	}

	function handleFirstOptionMouseLeave() {
		if (firstTooltip && firstOptionTooltipOnHover) {
			setFirstAnchorEl(null)
		}
	}

	function handleSecondOptionMouseEnter(e: React.MouseEvent<HTMLDivElement>) {
		if (secondTooltip && secondOptionTooltipOnHover && secondDisabled) {
			setSecondAnchorEl(e.currentTarget)
		}
	}

	function handleSecondOptionMouseLeave() {
		if (secondTooltip && secondOptionTooltipOnHover) {
			setSecondAnchorEl(null)
		}
	}

	return (
		<div className={styles.main} data-testid="SwitchButton">
			<div
				data-testid="switchButton_firstOption"
				className={`${styles.option} ${firstSelected ? styles.selected : ''} ${firstDisabled ? styles.disabled : ''}`}
				onClick={(e) => firstOptionClick(e)}
				onMouseEnter={handleFirstOptionMouseEnter}
				onMouseLeave={handleFirstOptionMouseLeave}
			>
				{firstOptionIcon && <Icon icon={firstOptionIcon} className={styles.icon} />}
				<p>{firstOption}</p>
			</div>
			<div
				data-testid="switchButton_secondOption"
				className={`${styles.option} ${!firstSelected ? styles.selected : ''} ${secondDisabled ? styles.disabled : ''}`}
				onClick={(e) => secondOptionClick(e)}
				onMouseEnter={handleSecondOptionMouseEnter}
				onMouseLeave={handleSecondOptionMouseLeave}
			>
				{secondOptionIcon && <Icon icon={secondOptionIcon} className={styles.icon} />}
				<p>{secondOption}</p>
			</div>
			{firstTooltip && firstTooltipProps && (
				<InfoTooltip
					anchorEl={firstAnchorEl}
					open={Boolean(firstAnchorEl)}
					onClose={() => setFirstAnchorEl(null)}
					text={firstTooltipProps.text}
					buttonLabel={firstTooltipProps.buttonLabel}
					onAction={firstTooltipProps.onAction}
					darkBackground={false}
					hover={true}
					enablePointerEvents={true}
				/>
			)}

			{secondTooltip && secondTooltipProps && (
				<InfoTooltip
					anchorEl={secondAnchorEl}
					open={Boolean(secondAnchorEl)}
					onClose={() => setSecondAnchorEl(null)}
					text={secondTooltipProps.text}
					buttonLabel={secondTooltipProps.buttonLabel}
					onAction={secondTooltipProps.onAction}
					darkBackground={false}
					hover={true}
					enablePointerEvents={true}
				/>
			)}
		</div>
	)
}